import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { Line, Html } from '@react-three/drei';
import { observer } from 'mobx-react-lite';
import { editorStore } from '../../store/editorStore';
import { floorplannerStore } from '../../store/floorplannerStore';
import { isRulerLineType, isSingleLineType, isSymbolType, isWallType, isDoubleDoorType, isDoorType, isSlidingDoorType, isWindowType, WallType, isCircleStairsType, isRectStairsType, isTurnStairsType, isAreaFillSymbolType, isAreaSymbolType, isVoidType } from '../../types/floorplannerTypes';
import { getSelectionBounds, isSelectionBoundsItem } from '../../utils/selectionBounds';
import { autorun, runInAction } from 'mobx';
import { projectToWorld } from '../FloorPlan/projectToWorld';
import { useDragSelectedObjects } from '../../hooks/useDragSelectedObjects';
import { FaRotate } from 'react-icons/fa6';
import { renderStore } from '../../store/renderStore';
import { wallUpdateManager } from '../../utils/wallUpdateManager';
import { updateSymbolAttachmentsDuringDrag, updateWallClippings } from '../FloorPlan/updateWallConnections';
import { MAX_INTERACTION_PLANE_SIZE, SELECTED_HANDLE_INTERACTION_Z, SELECTED_HANDLE_RENDER_ORDER } from '../../constants/canvas';
import { getBackdropHitTarget } from '../FloorPlan/BackdropImage';
import { findAreaFillAtPoint } from '../../utils/areaFillGeometry';
import { createEdgeConnection } from '../../utils/edgeConnectionHelpers';
import { ROTATION_GIZMO_SNAP_STEP } from '../../features/floorplan3d/interaction/symbol3DRotationGizmo';
import { getTurnStairsPlanBounds } from '../../features/floorplan3d/symbols/primitives/stairs/stairGeometry';
import { CANVAS_ACCENT_COLOR } from "../../constants/brandColors";
import { createDoubleClickDetector } from '../../utils/doubleClick';

const ROTATION_SESSION_EPSILON = 1e-6;
const CANVAS_HTML_HANDLE_Z_INDEX_RANGE = [0, 0];
const isRotationSessionActive = (angle: number) => Math.abs(angle) > ROTATION_SESSION_EPSILON;
const normalizeRotationAngle = (angle: number) => (
    THREE.MathUtils.euclideanModulo(angle + Math.PI, Math.PI * 2) - Math.PI
);

const SelectionPlane: React.FC = observer(() => {
    const { camera, gl } = useThree();
    const { startDraggingGroupSelection } = useDragSelectedObjects(gl, camera);
    const isSelecting = useRef(false);
    const startPoint = useRef<THREE.Vector3 | null>(null);
    const [endPoint, setEndPoint] = useState<THREE.Vector3 | null>(null);
    const isDrawingRef = useRef(false);
    const initialMousePosition = useRef<{ x: number; y: number } | null>(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [selectionRectanglePoints, setSelectionRectanglePoints] = useState<THREE.Vector3[]>([]);
    const noopRaycast = () => null;
    /** Lenient double-click for room fills (native dblclick fails at high mouse speed). */
    const areaFillDoubleClickRef = useRef(createDoubleClickDetector());

    // Add state to force updates during drag
    const [updateCounter, setUpdateCounter] = useState(0);

    // Setup autorun to track position changes
    useEffect(() => {
        const dispose = autorun(() => {
            // Track all selected objects' positions
            editorStore.selections.forEach(selection => {
                if (isSymbolType(selection)) {
                    const symbol = floorplannerStore.symbolsMap.get(selection.id);
                    if (symbol) {
                        // Access position to track changes
                        symbol.position.x;
                        symbol.position.y;
                    }
                } else if (isWallType(selection) || isSingleLineType(selection) || isRulerLineType(selection)) {
                    const obj = floorplannerStore.findObjectId(selection.id);
                    if (obj) {
                        // Access positions to track changes
                        obj.start.x;
                        obj.start.y;
                        obj.end.x;
                        obj.end.y;
                    }
                }
            });

            // Also track translate state changes to update immediately when translate ends
            // This prevents the group box from flashing at original position
            renderStore.isGroupTranslating;

            // Update synchronously to prevent flicker on group translate end.
            // Using requestAnimationFrame caused a 1-frame delay where the group box
            // would flash at the original position when translate ended.
            // React's built-in batching handles performance.
            setUpdateCounter(prev => prev + 1);
        });

        return () => {
            dispose();
        };
    }, []);

    const handlePointerDown = (event: PointerEvent) => {
        initialMousePosition.current = { x: event.clientX, y: event.clientY };

        // Only start selection if we are not in a construction mode or panning
        if (
            !editorStore.view3D &&
            !editorStore.wallConstructionMode &&
            !editorStore.lineConstructionMode &&
            !editorStore.rulerConstructionMode &&
            !editorStore.areaConstructionMode &&
            !editorStore.areaFillConstructionMode &&
            !editorStore.areaBleedFillMode &&
            !editorStore.panning
        ) {
            isSelecting.current = true;
            isDrawingRef.current = false;
            startPoint.current = getWorldPositionFromMouse(event.clientX, event.clientY);
            setSelectionRectanglePoints([]);

            // Define handlers for this specific drag session
            const onPointerMove = (moveEvent: PointerEvent) => {
                if (!isSelecting.current || !startPoint.current || editorStore.panning) return;

                // We use moveEvent.buttons check to ensure we stop if user released without us knowing, 
                // but window pointerup usually catches this. 
                // Note: moveEvent.buttons === 0 checks if no buttons are pressed.
                if (moveEvent.buttons === 0) {
                    onPointerUp(moveEvent);
                    return;
                }

                const deltaX = Math.abs(moveEvent.clientX - initialMousePosition.current!.x);
                const deltaY = Math.abs(moveEvent.clientY - initialMousePosition.current!.y);

                if (deltaX > 2 || deltaY > 2) {
                    if (!isDrawingRef.current) {
                        isDrawingRef.current = true;
                        setIsDrawing(true);
                        // Marquee drag is not a click — drop any pending first click.
                        areaFillDoubleClickRef.current.reset();
                    }

                    const currentEndPoint = getWorldPositionFromMouse(moveEvent.clientX, moveEvent.clientY);

                    if (currentEndPoint) {
                        setEndPoint(currentEndPoint);

                        // Create selection rectangle points directly from world positions
                        const points = [
                            startPoint.current,
                            new THREE.Vector3(currentEndPoint.x, startPoint.current.y, 0),
                            currentEndPoint,
                            new THREE.Vector3(startPoint.current.x, currentEndPoint.y, 0),
                            startPoint.current.clone()
                        ];

                        setSelectionRectanglePoints(points);
                    }
                }
            };

            const onPointerUp = (upEvent: PointerEvent) => {
                window.removeEventListener('pointermove', onPointerMove);
                window.removeEventListener('pointerup', onPointerUp);

                // Don't handle pointer up during construction modes (redundant check if logic flow is correct but keeps safety)
                if (editorStore.wallConstructionMode ||
                    editorStore.lineConstructionMode ||
                    editorStore.rulerConstructionMode ||
                    editorStore.areaConstructionMode ||
                    editorStore.areaFillConstructionMode ||
                    editorStore.areaBleedFillMode) {
                    initialMousePosition.current = null;
                    return;
                }

                // Click (no drag): clear selection, or select a room fill on lenient double-click.
                let selectedAreaFillViaDoubleClick = false;
                if (initialMousePosition.current) {
                    const deltaX = Math.abs(upEvent.clientX - initialMousePosition.current.x);
                    const deltaY = Math.abs(upEvent.clientY - initialMousePosition.current.y);

                    if (deltaX < 2 && deltaY < 2) {
                        if (
                            areaFillDoubleClickRef.current.record(
                                upEvent.clientX,
                                upEvent.clientY,
                            )
                        ) {
                            selectedAreaFillViaDoubleClick = trySelectAreaFillAtClient(
                                upEvent.clientX,
                                upEvent.clientY,
                            );
                        }
                        if (!selectedAreaFillViaDoubleClick) {
                            editorStore.clearSelections();
                        }
                    }
                }

                // If the user was selecting, perform the selection
                if (isSelecting.current && !editorStore.panning) {
                    isSelecting.current = false;

                    if (isDrawingRef.current && startPoint.current) {
                        // Recalculate end point from event to ensure accuracy
                        // or use the last known endPoint state if available, but event is fresher
                        const finalEndPoint = getWorldPositionFromMouse(upEvent.clientX, upEvent.clientY);
                        if (finalEndPoint) {
                            selectObjectsInBox(startPoint.current, finalEndPoint);
                        } else if (endPoint) {
                            selectObjectsInBox(startPoint.current, endPoint);
                        }
                    } else {
                        // Clear selection on single click (already handled by delta check above usually, but fallback)
                        // If delta was small, we cleared. If delta large but !isDrawingRef (impossible?), nothing.
                        // Actually if delta > 2, isDrawingRef becomes true.
                        // Do not clear after a successful room-fill double-click select.
                        if (!isDrawingRef.current && !selectedAreaFillViaDoubleClick) {
                            editorStore.clearSelections();
                        }
                    }

                    // Reset variables
                    setIsDrawing(false);
                    isDrawingRef.current = false;
                    initialMousePosition.current = null;
                    startPoint.current = null;
                    setEndPoint(null);
                    setSelectionRectanglePoints([]);
                }
            };

            window.addEventListener('pointermove', onPointerMove);
            window.addEventListener('pointerup', onPointerUp);
        }
    };

    // Kept empty / original placeholders if needed, but we removed the originals.
    const handlePointerMove = (event: PointerEvent) => { };
    const handlePointerUp = (event: PointerEvent) => { };

    /**
     * Select the smallest room floor fill under a screen point, if interaction is allowed.
     * @returns true when a fill was selected
     */
    const trySelectAreaFillAtClient = (clientX: number, clientY: number): boolean => {
        if (
            editorStore.view3D ||
            editorStore.wallConstructionMode ||
            editorStore.lineConstructionMode ||
            editorStore.rulerConstructionMode ||
            editorStore.areaConstructionMode ||
            editorStore.areaFillConstructionMode ||
            editorStore.areaBleedFillMode ||
            editorStore.panning ||
            floorplannerStore.lockedByOther
        ) {
            return false;
        }

        if (getBackdropHitTarget(clientX, clientY, gl, camera)) {
            return false;
        }

        const [worldX, worldY] = projectToWorld(clientX, clientY, gl, camera);
        const areaFill = findAreaFillAtPoint(floorplannerStore, new THREE.Vector2(worldX, worldY));
        if (!areaFill) {
            return false;
        }

        if (!editorStore.isShiftPressed) {
            editorStore.clearSelections();
        }
        editorStore.addSelection(areaFill);
        floorplannerStore.updateSymbolProperty(areaFill.id, 'selected', true);
        return true;
    };

    /**
     * Room floor fills are intentionally not hit-tested on single click so marquee
     * selection works over them. Double-click on empty canvas over a fill selects it.
     * Lenient detector runs on pointer-up; native dblclick remains a fallback.
     */
    const handleDoubleClick = (event: { clientX: number; clientY: number; stopPropagation: () => void }) => {
        if (trySelectAreaFillAtClient(event.clientX, event.clientY)) {
            event.stopPropagation();
        }
    };

    const getWorldPositionFromMouse = (clientX: number, clientY: number): THREE.Vector3 | null => {
        const rect = gl.domElement.getBoundingClientRect();
        const ndcX = ((clientX - rect.left) / rect.width) * 2 - 1;
        const ndcY = -((clientY - rect.top) / rect.height) * 2 + 1;

        // Create a ray from the camera through the mouse point
        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);

        // Calculate the intersection with the z=0 plane
        const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1));
        const target = new THREE.Vector3();
        raycaster.ray.intersectPlane(plane, target);

        return target;
    };

    const projectToScreen = (vector: THREE.Vector3): THREE.Vector2 => {
        const projected = vector.clone().project(camera);
        const rect = gl.domElement.getBoundingClientRect();

        return new THREE.Vector2(
            (projected.x + 1) * 0.5 * rect.width + rect.left,
            (1 - projected.y) * 0.5 * rect.height + rect.top // Corrected Y calculation
        );
    };

    const selectObjectsInBox = (start: THREE.Vector3, end: THREE.Vector3) => {
        const walls = floorplannerStore.wallsMap;
        const symbols = floorplannerStore.symbolsMap;

        const screenStart = projectToScreen(start);
        const screenEnd = projectToScreen(end);

        const minX = Math.min(screenStart.x, screenEnd.x);
        const maxX = Math.max(screenStart.x, screenEnd.x);
        const minY = Math.min(screenStart.y, screenEnd.y);
        const maxY = Math.max(screenStart.y, screenEnd.y);

        // Clear previous selection
        editorStore.clearSelections();

        // Create selection rectangle in screen space
        const selectionRect = new THREE.Box2(
            new THREE.Vector2(minX, minY),
            new THREE.Vector2(maxX, maxY)
        );

        // Helper function to check if a point is inside selection rectangle
        const isPointInSelection = (point: THREE.Vector2): boolean => {
            return point.x >= minX && point.x <= maxX && point.y >= minY && point.y <= maxY;
        };

        // Helper function to check if a line segment intersects with selection rectangle
        const isLineInSelection = (start: THREE.Vector2, end: THREE.Vector2): boolean => {
            // Check if either endpoint is inside selection
            if (isPointInSelection(start) || isPointInSelection(end)) return true;

            // Check if line intersects any of the selection rectangle edges
            const rectEdges = [
                [new THREE.Vector2(minX, minY), new THREE.Vector2(maxX, minY)],
                [new THREE.Vector2(maxX, minY), new THREE.Vector2(maxX, maxY)],
                [new THREE.Vector2(maxX, maxY), new THREE.Vector2(minX, maxY)],
                [new THREE.Vector2(minX, maxY), new THREE.Vector2(minX, minY)]
            ];

            for (const [edgeStart, edgeEnd] of rectEdges) {
                if (lineSegmentsIntersect(start, end, edgeStart, edgeEnd)) {
                    return true;
                }
            }

            return false;
        };

        // Helper function to check if two line segments intersect
        const lineSegmentsIntersect = (
            line1Start: THREE.Vector2,
            line1End: THREE.Vector2,
            line2Start: THREE.Vector2,
            line2End: THREE.Vector2
        ): boolean => {
            const det = (line1End.x - line1Start.x) * (line2End.y - line2Start.y) -
                (line2End.x - line2Start.x) * (line1End.y - line1Start.y);

            if (det === 0) return false;

            const lambda = ((line2End.y - line2Start.y) * (line2End.x - line1Start.x) +
                (line2Start.x - line2End.x) * (line2End.y - line1Start.y)) / det;
            const gamma = ((line1Start.y - line1End.y) * (line2End.x - line1Start.x) +
                (line1End.x - line1Start.x) * (line2End.y - line1Start.y)) / det;

            return (lambda > 0 && lambda < 1) && (gamma > 0 && gamma < 1);
        };

        // Process walls first
        Array.from(walls.values()).forEach((wall) => {
            const startWorld = new THREE.Vector3(wall.start.x, wall.start.y, 0);
            const endWorld = new THREE.Vector3(wall.end.x, wall.end.y, 0);
            const startScreen = projectToScreen(startWorld);
            const endScreen = projectToScreen(endWorld);

            // Check if wall line intersects with selection rectangle
            if (isLineInSelection(startScreen, endScreen)) {
                floorplannerStore.selectWall(wall.id);
                editorStore.addSelection(wall);
            }
        });

        // Process symbols
        Array.from(symbols.values()).forEach((symbol) => {
            if (!symbol) return;
            // Room floors are double-click only — never include them in marquee selection.
            if (isAreaFillSymbolType(symbol)) return;
            // New: Exclude locked backdrops (or any locked symbol) from marquee selection
            if ((symbol as any)?.locked) return;

            // Get the symbol's world position
            const worldPos = new THREE.Vector3(symbol.position.x, symbol.position.y, 0);
            const screenPos = projectToScreen(worldPos);

            // Calculate symbol bounds based on type and properties
            let bounds = getSymbolBounds(symbol, screenPos);

            // Check if symbol bounds intersect with selection rectangle
            if (bounds && doBoxesIntersect(selectionRect, bounds)) {
                floorplannerStore.selectSymbol(symbol.id);
                editorStore.addSelection(symbol);
            }
        });

        // Process single lines
        Array.from(floorplannerStore.singleLinesMap.values()).forEach((line) => {
            const startWorld = new THREE.Vector3(line.start.x, line.start.y, 0);
            const endWorld = new THREE.Vector3(line.end.x, line.end.y, 0);
            const startScreen = projectToScreen(startWorld);
            const endScreen = projectToScreen(endWorld);

            if (isLineInSelection(startScreen, endScreen)) {
                floorplannerStore.selectLine(line.id);
                editorStore.addSelection(line);
            }
        });

        // Process ruler lines
        Array.from(floorplannerStore.rulerLinesMap.values()).forEach((line) => {
            const startWorld = new THREE.Vector3(line.start.x, line.start.y, 0);
            const endWorld = new THREE.Vector3(line.end.x, line.end.y, 0);
            const startScreen = projectToScreen(startWorld);
            const endScreen = projectToScreen(endWorld);

            if (isLineInSelection(startScreen, endScreen)) {
                floorplannerStore.selectRuler(line.id);
                editorStore.addSelection(line);
            }
        });
    };

    // Helper function to determine door swing direction
    const swingsRight = (symbol: any): boolean => {
        if (!symbol.attachedTo) return true; // Default to right if not attached
        const wall = floorplannerStore.wallsMap.get(symbol.attachedTo);
        if (!wall) return true;

        // Calculate wall direction vector
        const wallDir = new THREE.Vector2()
            .subVectors(new THREE.Vector2(wall.end.x, wall.end.y), new THREE.Vector2(wall.start.x, wall.start.y))
            .normalize();

        // Calculate door position relative to wall start
        const doorPos = new THREE.Vector2()
            .subVectors(new THREE.Vector2(symbol.position.x, symbol.position.y), new THREE.Vector2(wall.start.x, wall.start.y));

        // Cross product to determine which side of the wall the door is on
        const cross = wallDir.x * doorPos.y - wallDir.y * doorPos.x;
        return cross > 0;
    };

    const getSymbolBounds = (symbol: any, screenPos: THREE.Vector2): THREE.Box2 | null => {
        // Get world space points based on symbol type
        let worldPoints: THREE.Vector3[] = [];

        if (isWindowType(symbol)) {
            const width = symbol.windowLength;
            const height = symbol.windowWidth;
            // Windows are centered, create points around center
            worldPoints = [
                new THREE.Vector3(-width / 2, -height / 2, 0),
                new THREE.Vector3(width / 2, -height / 2, 0),
                new THREE.Vector3(width / 2, height / 2, 0),
                new THREE.Vector3(-width / 2, height / 2, 0)
            ];
        } else if (isVoidType(symbol)) {
            const width = symbol.voidLength;
            const height = symbol.voidWidth;
            // Voids use a centered invisible box of voidLength x voidWidth for pointer hits.
            worldPoints = [
                new THREE.Vector3(-width / 2, -height / 2, 0),
                new THREE.Vector3(width / 2, -height / 2, 0),
                new THREE.Vector3(width / 2, height / 2, 0),
                new THREE.Vector3(-width / 2, height / 2, 0)
            ];
        } else if (isSlidingDoorType(symbol)) {
            const width = symbol.doorWidth * 2;
            const height = symbol.doorWidth;

            worldPoints = [
                new THREE.Vector3(0, 0, 0),
                new THREE.Vector3(width, 0, 0),
                new THREE.Vector3(width, height, 0),
                new THREE.Vector3(0, height, 0)
            ];
        } else if (isDoorType(symbol)) {
            const width = symbol.doorWidth;
            const height = width; // Height equals width for doors

            // Door frame points only (from bottom-left origin)
            worldPoints = [
                new THREE.Vector3(0, 0, 0),
                new THREE.Vector3(width, 0, 0),
                new THREE.Vector3(width, height, 0),
                new THREE.Vector3(0, height, 0)
            ];
        } else if (isDoubleDoorType(symbol)) {
            const leafWidth = symbol.doubleDoorWidth; // Width of a single leaf
            const height = leafWidth;

            // Door frame points only
            worldPoints = [
                new THREE.Vector3(0, 0, 0),
                new THREE.Vector3(leafWidth * 2, 0, 0),
                new THREE.Vector3(leafWidth * 2, height, 0),
                new THREE.Vector3(0, height, 0)
            ];
        } else if (isCircleStairsType(symbol)) {
            // Calculate points along the arc at small intervals
            const numPoints = 36;  // Check every 10 degrees
            const flipX = symbol.flipHorizontal ? -1 : 1;
            const flipY = symbol.flipVertical ? -1 : 1;

            // Always include the starting point (right side, 0 degrees)
            worldPoints.push(new THREE.Vector3(
                symbol.position.x + symbol.circleStairsWidth * flipX,
                symbol.position.y,
                0
            ));

            // Sample points along the arc
            for (let i = 0; i <= numPoints; i++) {
                const angle = (i / numPoints) * symbol.openAngle;
                const x = symbol.position.x + symbol.circleStairsWidth * Math.cos(angle) * flipX;
                const y = symbol.position.y + symbol.circleStairsWidth * Math.sin(angle) * flipY;
                worldPoints.push(new THREE.Vector3(x, y, 0));
            }
        } else if (isAreaSymbolType(symbol) || isAreaFillSymbolType(symbol)) {
            worldPoints = Array.isArray(symbol.vertices)
                ? symbol.vertices.map((vertex) => new THREE.Vector3(vertex.x, vertex.y, 0))
                : [];
        } else if (isRectStairsType(symbol)) {
            const width = symbol.rectStairsWidth;
            const height = symbol.rectStairsHeight;
            const flipX = symbol.flipHorizontal ? -1 : 1;
            const flipY = symbol.flipVertical ? -1 : 1;

            // Create points for the rectangular stairs
            worldPoints = [
                new THREE.Vector3(-width / 2 * flipX, -height / 2 * flipY, 0),
                new THREE.Vector3(width / 2 * flipX, -height / 2 * flipY, 0),
                new THREE.Vector3(width / 2 * flipX, height / 2 * flipY, 0),
                new THREE.Vector3(-width / 2 * flipX, height / 2 * flipY, 0),
                new THREE.Vector3(-width / 2 * flipX, -height / 2 * flipY, 0)  // Close the loop
            ];

            // Move points to world position before rotation
            worldPoints.forEach(point => {
                point.add(new THREE.Vector3(symbol.position.x, symbol.position.y, 0));
            });

            // Apply rotation if present
            if (symbol.rotation !== undefined) {
                const rotationPoint = new THREE.Vector3(symbol.position.x, symbol.position.y, 0);
                worldPoints.forEach(point => {
                    point.sub(rotationPoint);
                    point.applyAxisAngle(new THREE.Vector3(0, 0, 1), symbol.rotation || 0);
                    point.add(rotationPoint);
                });
            }

            // Skip the general rotation and position application since we've already done it
            return createBoundsFromPoints(worldPoints.map(p => projectToScreen(p)));
        } else if (isTurnStairsType(symbol)) {
            const bounds = getTurnStairsPlanBounds(symbol, {
                defaultStepSize: symbol.stairStepSize ?? 0.32,
            });
            const width = bounds.width;
            const height = bounds.depth;
            const flipX = symbol.flipHorizontal ? -1 : 1;
            const flipY = symbol.flipVertical ? -1 : 1;

            worldPoints = [
                new THREE.Vector3(-width / 2 * flipX, -height / 2 * flipY, 0),
                new THREE.Vector3(width / 2 * flipX, -height / 2 * flipY, 0),
                new THREE.Vector3(width / 2 * flipX, height / 2 * flipY, 0),
                new THREE.Vector3(-width / 2 * flipX, height / 2 * flipY, 0),
                new THREE.Vector3(-width / 2 * flipX, -height / 2 * flipY, 0),
            ];

            worldPoints.forEach(point => {
                point.add(new THREE.Vector3(symbol.position.x, symbol.position.y, 0));
            });

            if (symbol.rotation !== undefined) {
                const rotationPoint = new THREE.Vector3(symbol.position.x, symbol.position.y, 0);
                worldPoints.forEach(point => {
                    point.sub(rotationPoint);
                    point.applyAxisAngle(new THREE.Vector3(0, 0, 1), symbol.rotation || 0);
                    point.add(rotationPoint);
                });
            }

            return createBoundsFromPoints(worldPoints.map(p => projectToScreen(p)));
        } else {
            // Handle specific symbol types with their dimensions
            let width = 1;
            let height = 1;
            let flipX = 1;
            let flipY = 1;

            if ('squareWidth' in symbol) {
                width = symbol.squareWidth;
                height = symbol.squareHeight;
                flipX = symbol.flipHorizontal ? -1 : 1;
                flipY = symbol.flipVertical ? -1 : 1;
            } else if ('triangleWidth' in symbol) {
                width = symbol.triangleWidth;
                height = symbol.triangleHeight;
                flipX = symbol.flipHorizontal ? -1 : 1;
                flipY = symbol.flipVertical ? -1 : 1;
            } else if ('circleWidth' in symbol) {
                width = symbol.circleWidth;
                height = symbol.circleHeight;
                flipX = symbol.flipHorizontal ? -1 : 1;
                flipY = symbol.flipVertical ? -1 : 1;
            } else if ('polygonWidth' in symbol) {
                width = symbol.polygonWidth;
                height = symbol.polygonHeight;
                flipX = symbol.flipHorizontal ? -1 : 1;
                flipY = symbol.flipVertical ? -1 : 1;
            } else {
                width = (symbol as any)?.width ?? 1;
                height = (symbol as any)?.height ?? 1;
            }

            worldPoints = [
                new THREE.Vector3(-width / 2 * flipX, -height / 2 * flipY, 0),
                new THREE.Vector3(width / 2 * flipX, -height / 2 * flipY, 0),
                new THREE.Vector3(width / 2 * flipX, height / 2 * flipY, 0),
                new THREE.Vector3(-width / 2 * flipX, height / 2 * flipY, 0)
            ];
        }

        // Apply rotation if present
        if (symbol.rotation !== undefined) {
            const rotationMatrix = new THREE.Matrix4();
            const rotationPoint = new THREE.Vector3(symbol.position.x, symbol.position.y, 0);

            if (isRectStairsType(symbol) || isTurnStairsType(symbol)) {
                // Rect/turn stairs rotate around center
                worldPoints.forEach(point => {
                    point.sub(rotationPoint);
                    point.applyAxisAngle(new THREE.Vector3(0, 0, 1), symbol.rotation || 0);
                    point.add(rotationPoint);
                });
            } else if (isWindowType(symbol) || (!isDoorType(symbol) && !isSlidingDoorType(symbol))) {
                // Windows and other symbols rotate around center
                rotationMatrix.makeRotationZ(symbol.rotation || 0);
                worldPoints.forEach(point => point.applyMatrix4(rotationMatrix));
            } else {
                // Doors rotate around bottom-left
                rotationMatrix.makeRotationZ(symbol.rotation || 0);
                worldPoints.forEach(point => point.applyMatrix4(rotationMatrix));
            }
        }

        // Move to world position (skip for circle stairs and rect/turn stairs as we already included position)
        if (!isCircleStairsType(symbol) && !isRectStairsType(symbol) && !isTurnStairsType(symbol)) {
            worldPoints.forEach(point => {
                point.add(new THREE.Vector3(symbol.position.x, symbol.position.y, 0));
            });
        }

        // Project to screen space
        const screenPoints = worldPoints.map(p => projectToScreen(p));

        return createBoundsFromPoints(screenPoints);
    };

    // Helper function to create bounds from points
    const createBoundsFromPoints = (points: THREE.Vector2[]): THREE.Box2 => {
        const bounds = new THREE.Box2();
        points.forEach(p => bounds.expandByPoint(p));
        return bounds;
    };

    // Helper function to check if two boxes intersect
    const doBoxesIntersect = (box1: THREE.Box2, box2: THREE.Box2): boolean => {
        return !(
            box1.max.x < box2.min.x ||
            box1.min.x > box2.max.x ||
            box1.max.y < box2.min.y ||
            box1.min.y > box2.max.y
        );
    };

    useEffect(() => {
        if (editorStore.selections.length === 0) {
            setSelectionRectanglePoints([]);
        }
    }, [editorStore.selections]);

    // Memoize the bounding box calculation to prevent unnecessary recalculations
    const boundingBoxPoints = useMemo(() => {
        if (editorStore.selections.length <= 1) return [];
        if (isDrawing) return [];

        const bounds = getSelectionBounds(editorStore.selections.filter(isSelectionBoundsItem));
        return bounds ? bounds.outline : [];
    }, [editorStore.selections, updateCounter, selectionRectanglePoints, isDrawing]);

    // Helper derived data for the multi-select bounding box
    const groupBox = useMemo(() => {
        if (boundingBoxPoints.length < 4) return null;
        const minX = boundingBoxPoints[0].x;
        const minY = boundingBoxPoints[0].y;
        const maxX = boundingBoxPoints[2].x;
        const maxY = boundingBoxPoints[2].y;
        const width = maxX - minX;
        const height = maxY - minY;

        // Validate that all values are finite numbers
        if (!Number.isFinite(minX) || !Number.isFinite(minY) || !Number.isFinite(maxX) || !Number.isFinite(maxY) ||
            !Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
            console.warn('SelectionPlane: Invalid groupBox calculation detected', {
                minX, minY, maxX, maxY, width, height
            });
            return null;
        }

        const center = new THREE.Vector3(minX + width / 2, minY + height / 2, 0);
        return { minX, minY, maxX, maxY, width, height, center };
    }, [boundingBoxPoints]);

    // Detect if any wall is selected (kept for reference)
    const anyWallSelected = useMemo(() => editorStore.selections.some(s => isWallType(s)), [editorStore.selections]);

    // New code: Only allow rotate/resize if all selected items are independent
    // - Symbols: cannot be attached to a wall that is NOT in selection
    // - Walls: cannot have connections to walls NOT in selection
    const selectionIndependent = useMemo(() => {
        const selectedIds = new Set(editorStore.selections.map(s => s.id));
        for (const sel of editorStore.selections) {
            if (isSymbolType(sel)) {
                const sym = floorplannerStore.symbolsMap.get(sel.id);
                // Exclude locked backdrops entirely from transform eligibility and dependency constraints
                if ((sym as any)?.locked) {
                    return false;
                }
                if (sym && (sym as any).attachedTo && !selectedIds.has((sym as any).attachedTo)) {
                    return false;
                }
            } else if (isWallType(sel)) {
                const wall = floorplannerStore.wallsMap.get(sel.id);
                if (wall?.centerConnections?.length) {
                    for (const c of wall.centerConnections) {
                        if (!selectedIds.has(c.id)) return false;
                    }
                }
            }
        }
        return true;
    }, [editorStore.selections]);

    // ----- Group Move by dragging inside the box -----
    // We draw a transparent plane over the group box and start the existing group-drag logic
    const onGroupPlanePointerDown = useCallback((e: import('@react-three/fiber').ThreeEvent<PointerEvent>) => {
        // Safety: only if actually have a bbox
        if (!groupBox) return;
        // Start unified dragging for the selection
        // NOTE: The hook handles collecting originals and applying deltas per selected object
        startDraggingGroupSelection(e);
    }, [groupBox, startDraggingGroupSelection]);

    // ----- Group Rotate -----
    // Keep original states for smooth deltas
    const rotationStartAngle = useRef<number>(0);
    const originalSymbolStates = useRef<Map<string, { position: THREE.Vector2; rotation?: number }>>(new Map());
    // New code: store original start/end for line-like objects (walls, single lines, ruler lines) during group rotation
    const originalLineStates = useRef<Map<string, { start: THREE.Vector2; end: THREE.Vector2 }>>(new Map());
    const isRotating = useRef<boolean>(false);
    const [rotationPreview, setRotationPreview] = useState<number>(0);
    // Keep the selection box size stable during rotation to avoid jitter/misalignment
    const rotateStartBoxRef = useRef<{ width: number; height: number; center: THREE.Vector3 } | null>(null);
    // Rotation session: persist rotated box/angle until deselection
    const rotationSessionAngleRef = useRef<number>(0);
    const rotationSessionBoxRef = useRef<{ width: number; height: number; center: THREE.Vector3 } | null>(null);
    const rotationDeltaRef = useRef<number>(0);
    const rotationRafId = useRef<number | null>(null);
    const pendingRotationDeltaRef = useRef<number>(0);
    const rotationDragWallIds = useRef<string[]>([]);
    const translateStartCenterRef = useRef<THREE.Vector2 | null>(null);
    const wasGroupTranslatingRef = useRef(false);

    const beginGroupRotate = useCallback((e: import('@react-three/fiber').ThreeEvent<PointerEvent>) => {
        if (!groupBox) return;
        e.stopPropagation();
        // Do not rotate if selection depends on unselected attachments
        if (!selectionIndependent) return;

        // Begin delta-based undo tracking for group rotation
        floorplannerStore.beginUndoTransaction('rotateGroup');

        rotationDragWallIds.current = [];

        // Collect object IDs and wall IDs for visual rotation mode
        const objectIds: string[] = [];
        const wallIds: string[] = [];
        const selectedWallIds = new Set(
            editorStore.selections
                .filter((sel) => isWallType(sel))
                .map((sel) => sel.id)
        );

        // Cache original symbol states and record for undo
        originalSymbolStates.current.clear();
        originalLineStates.current.clear();
        editorStore.selections.forEach(sel => {
            objectIds.push(sel.id);
            if (isSymbolType(sel)) {
                floorplannerStore.recordSymbolForUndo(sel.id);
                const attachedWall = floorplannerStore.symbolIsAttached(sel.id);
                if (attachedWall && !selectedWallIds.has(attachedWall)) {
                    floorplannerStore.recordWallForUndo(attachedWall);
                }
                const sym = floorplannerStore.symbolsMap.get(sel.id);
                if (sym) {
                    originalSymbolStates.current.set(sel.id, {
                        position: sym.position.clone(),
                        rotation: sym.rotation || 0,
                    });
                }
            } else if (isWallType(sel) || isSingleLineType(sel) || isRulerLineType(sel)) {
                // Record original start/end for walls/lines/rulers so we can rotate them as a group
                floorplannerStore.recordObjectForUndo(sel.id);
                const obj = floorplannerStore.findObjectId(sel.id);
                if (obj) {
                    originalLineStates.current.set(sel.id, {
                        start: obj.start.clone(),
                        end: obj.end.clone(),
                    });
                }
            }
            if (isWallType(sel)) {
                wallIds.push(sel.id);
                rotationDragWallIds.current.push(sel.id);
            }
        });

        // Choose a stable center for the whole rotation gesture (session center if present)
        const activeCenter = (rotationSessionBoxRef.current?.center ?? groupBox.center).clone();

        // Begin visual rotation mode - blocks MobX reactions during drag
        renderStore.beginGroupRotate(objectIds, wallIds, { x: activeCenter.x, y: activeCenter.y });

        // Initial angle from active center to pointer
        const [wx, wy] = projectToWorld(e.clientX, e.clientY, gl, camera);
        rotationStartAngle.current = Math.atan2(wy - activeCenter.y, wx - activeCenter.x);
        isRotating.current = true;
        // Lock the selection box dimensions for the duration of rotation (use session box if present)
        const baseBox = rotationSessionBoxRef.current ?? { width: groupBox.width, height: groupBox.height, center: activeCenter.clone() };
        rotateStartBoxRef.current = { width: baseBox.width, height: baseBox.height, center: baseBox.center.clone() };
        rotationDeltaRef.current = 0;

        const onMove = (me: PointerEvent) => {
            if (!isRotating.current) return;
            const [cx, cy] = projectToWorld(me.clientX, me.clientY, gl, camera);
            const currentAngle = Math.atan2(cy - activeCenter.y, cx - activeCenter.x);
            let delta = currentAngle - rotationStartAngle.current;
            // Normalize across PI boundary
            if (delta > Math.PI) delta -= 2 * Math.PI;
            if (delta < -Math.PI) delta += 2 * Math.PI;

            // Match 3D rotation: Shift snaps to 45° increments, otherwise free rotation
            const baseAngle = rotationSessionAngleRef.current || 0;
            let appliedDelta = delta;
            if (editorStore.isShiftPressed) {
                const total = baseAngle + delta;
                const snapped =
                    Math.round(total / ROTATION_GIZMO_SNAP_STEP) *
                    ROTATION_GIZMO_SNAP_STEP;
                appliedDelta = snapped - baseAngle;
            }

            pendingRotationDeltaRef.current = appliedDelta;
            if (rotationRafId.current !== null) {
                return;
            }

            rotationRafId.current = requestAnimationFrame(() => {
                rotationRafId.current = null;
                const nextDelta = pendingRotationDeltaRef.current;

                // OPTIMIZED: Only update the rotation angle, not MobX observables.
                // Components apply this angle visually at render time.
                renderStore.updateGroupRotateAngle(nextDelta);
                setRotationPreview(nextDelta);
                rotationDeltaRef.current = nextDelta;
            });
        };

        const onUp = () => {
            isRotating.current = false;
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);

            if (rotationRafId.current !== null) {
                cancelAnimationFrame(rotationRafId.current);
                rotationRafId.current = null;
                const nextDelta = pendingRotationDeltaRef.current;
                renderStore.updateGroupRotateAngle(nextDelta);
                setRotationPreview(nextDelta);
                rotationDeltaRef.current = nextDelta;
            }

            // Get final rotation angle before ending rotate mode
            const finalAngle = rotationDeltaRef.current;

            // Helper to rotate a point around the center
            const rotatePoint = (p: THREE.Vector2) => {
                const off = new THREE.Vector2(p.x - activeCenter.x, p.y - activeCenter.y);
                const cos = Math.cos(finalAngle);
                const sin = Math.sin(finalAngle);
                return new THREE.Vector2(
                    activeCenter.x + off.x * cos - off.y * sin,
                    activeCenter.y + off.x * sin + off.y * cos
                );
            };

            // Commit all final positions to MobX in one batch
            // NOTE: Keep rotate mode active during position commit to prevent flash at original position
            runInAction(() => {
                // First pass: Update all walls/lines
                originalLineStates.current.forEach((state, id) => {
                    const ns = rotatePoint(state.start);
                    const ne = rotatePoint(state.end);
                    floorplannerStore.updateObjectPosition(id, new THREE.Vector2(ns.x, ns.y), new THREE.Vector2(ne.x, ne.y), { bypassEdgeConstraints: true });
                    floorplannerStore.markObjectForRoomUpdate(id);
                });

                // Second pass: Update symbols
                originalSymbolStates.current.forEach((state, id) => {
                    const pos = state.position.clone();
                    const offset = new THREE.Vector2(pos.x - activeCenter.x, pos.y - activeCenter.y);
                    const cos = Math.cos(finalAngle);
                    const sin = Math.sin(finalAngle);
                    const rx = offset.x * cos - offset.y * sin;
                    const ry = offset.x * sin + offset.y * cos;
                    const newPos = new THREE.Vector2(activeCenter.x + rx, activeCenter.y + ry);
                    const movedAttachedSymbol = floorplannerStore.updateSymbolPositionRespectingAttachment(id, [newPos.x, newPos.y]);
                    if (!movedAttachedSymbol) {
                        floorplannerStore.updateSymbolProperty(id, 'rotation', (state.rotation || 0) + finalAngle);
                    }
                });
            });

            // Force recompose wall shapes and update clippings BEFORE ending rotate mode
            // This prevents a flash where walls appear at original positions
            rotationDragWallIds.current.forEach(wallId => {
                updateWallClippings(wallId);
                renderStore.clearWallShapes(wallId);
                renderStore.composeWallShape(wallId, undefined, false);
            });

            // Process room updates SYNCHRONOUSLY before ending rotate mode
            floorplannerStore.updateAffectedRooms();

            // End rotate mode AFTER wall shapes are recomposed AND room centroids updated
            renderStore.endGroupRotate();

            floorplannerStore.setDirty();
            setRotationPreview(0);
            // Persist session angle and box; keep overlay rotated until deselection
            const nextAngle = normalizeRotationAngle((rotationSessionAngleRef.current || 0) + finalAngle);
            rotationSessionAngleRef.current = isRotationSessionActive(nextAngle) ? nextAngle : 0;
            rotationSessionBoxRef.current = isRotationSessionActive(rotationSessionAngleRef.current)
                ? rotateStartBoxRef.current
                : null;
            rotateStartBoxRef.current = null;
            rotationDeltaRef.current = 0;

            // Record final state of all rotated objects for undo
            const changedAttachmentWallIds = new Set<string>();
            editorStore.selections.forEach(sel => {
                if (isSymbolType(sel) || isWallType(sel) || isSingleLineType(sel) || isRulerLineType(sel)) {
                    if (isSymbolType(sel)) {
                        const attachedWall = floorplannerStore.symbolIsAttached(sel.id);
                        if (attachedWall && !selectedWallIds.has(attachedWall)) {
                            changedAttachmentWallIds.add(attachedWall);
                        }
                    }
                    floorplannerStore.recordObjectChange(sel.id, 'modify');
                }
            });
            changedAttachmentWallIds.forEach((wallId) => {
                floorplannerStore.recordWallChange(wallId, 'modify');
            });
            floorplannerStore.pushToUndoStack();

            rotationDragWallIds.current = [];
        };

        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
    }, [selectionIndependent, groupBox, gl, camera]);

    // ----- Group Resize -----
    // We scale symbols about an anchor corner; walls are excluded (and presence of walls disables handles)
    type SizeKeys = { wKey?: string; hKey?: string };
    const getSizeKeys = (symbol: any): SizeKeys => {
        // Return width/height property keys for common symbol types
        if ('squareWidth' in symbol && 'squareHeight' in symbol) return { wKey: 'squareWidth', hKey: 'squareHeight' };
        if ('triangleWidth' in symbol && 'triangleHeight' in symbol) return { wKey: 'triangleWidth', hKey: 'triangleHeight' };
        if ('circleWidth' in symbol && 'circleHeight' in symbol) return { wKey: 'circleWidth', hKey: 'circleHeight' };
        if ('polygonWidth' in symbol && 'polygonHeight' in symbol) return { wKey: 'polygonWidth', hKey: 'polygonHeight' };
        if ('rectStairsWidth' in symbol && 'rectStairsHeight' in symbol) return { wKey: 'rectStairsWidth', hKey: 'rectStairsHeight' };
        // Generic fallback
        if ('width' in symbol && 'height' in symbol) return { wKey: 'width', hKey: 'height' };
        return {};
    };

    const resizeStartRef = useRef<{
        anchorLocal: THREE.Vector2;
        center: THREE.Vector3;
        angle: number;
        startBox: { width: number; height: number };
        pointerOffsetLocal: THREE.Vector2;
        initialAspectRatio: number;
    } | null>(null);
    const originalSymbolStatesForResize = useRef<Map<string, { position: THREE.Vector2; width?: number; height?: number }>>(new Map());
    // Store original wall/line/ruler endpoints, lengths, and symbol attachments for group resize
    const originalLineStatesForResize = useRef<Map<string, {
        start: THREE.Vector2;
        end: THREE.Vector2;
        length: number;
        symbolAttachments?: Array<{ symbolId: string; positionFromStart: number }>;
    }>>(new Map());
    const isResizing = useRef<boolean>(false);
    const resizeDragWallIds = useRef<string[]>([]);
    // Store endpoint connection groups computed at drag start (not on every move)
    // Maps group representative key -> Set of endpoint keys in that group
    const endpointConnectionGroupsRef = useRef<Map<string, Set<string>>>(new Map());
    // Maps endpoint key -> the group representative key
    const endpointToGroupRef = useRef<Map<string, string>>(new Map());

    const beginGroupResize = useCallback((corner: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight', e: import('@react-three/fiber').ThreeEvent<PointerEvent>) => {
        if (!groupBox) return;
        e.stopPropagation();
        // Disable resize if selection depends on unselected attachments
        if (!selectionIndependent) return;

        // Begin delta-based undo tracking for group resize
        floorplannerStore.beginUndoTransaction('resizeGroup');

        resizeDragWallIds.current = [];
        const selectedWallIdsAtResizeStart = new Set(
            editorStore.selections
                .filter((sel) => isWallType(sel))
                .map((sel) => sel.id)
        );

        // Set global dragging state for performance and tool behavior
        renderStore.setIsDragging(true);

        // ... existing local coordinate setup ...

        // Record objects for undo before modification
        editorStore.selections.forEach(sel => {
            if (isSymbolType(sel)) {
                floorplannerStore.recordSymbolForUndo(sel.id);
                const attachedWall = floorplannerStore.symbolIsAttached(sel.id);
                if (attachedWall && !selectedWallIdsAtResizeStart.has(attachedWall)) {
                    floorplannerStore.recordWallForUndo(attachedWall);
                }
            } else if (isWallType(sel) || isSingleLineType(sel) || isRulerLineType(sel)) {
                floorplannerStore.recordObjectForUndo(sel.id);
            }
        });

        // Use rotation session angle and center for local-space resize (angle can be 0)
        const baseCenter = (rotationSessionBoxRef.current?.center ?? groupBox.center).clone();
        const baseWidth = rotationSessionBoxRef.current?.width ?? groupBox.width;
        const baseHeight = rotationSessionBoxRef.current?.height ?? groupBox.height;
        const angle = rotationSessionAngleRef.current || 0;

        // Define local corners in box space (-w/2,-h/2) bottomLeft, etc.
        const halfW = baseWidth / 2;
        const halfH = baseHeight / 2;
        let startCornerLocal = new THREE.Vector2();
        let anchorLocal = new THREE.Vector2();
        if (corner === 'topLeft') {
            startCornerLocal.set(-halfW, halfH);
            anchorLocal.set(halfW, -halfH);
        } else if (corner === 'topRight') {
            startCornerLocal.set(halfW, halfH);
            anchorLocal.set(-halfW, -halfH);
        } else if (corner === 'bottomLeft') {
            startCornerLocal.set(-halfW, -halfH);
            anchorLocal.set(halfW, halfH);
        } else {
            // bottomRight
            startCornerLocal.set(halfW, -halfH);
            anchorLocal.set(-halfW, halfH);
        }

        // Helper to convert world->local for current angle and center
        const worldToLocal = (wx: number, wy: number) => {
            const dx = wx - baseCenter.x;
            const dy = wy - baseCenter.y;
            const cos = Math.cos(-angle);
            const sin = Math.sin(-angle);
            return new THREE.Vector2(dx * cos - dy * sin, dx * sin + dy * cos);
        };

        const [ipx, ipy] = projectToWorld(e.clientX, e.clientY, gl, camera);
        const initialPointerLocal = worldToLocal(ipx, ipy);
        const pointerOffsetLocal = startCornerLocal.clone().sub(initialPointerLocal);

        // Store initial aspect ratio and start state in local coordinates
        resizeStartRef.current = {
            anchorLocal,
            center: baseCenter,
            angle,
            startBox: { width: baseWidth, height: baseHeight },
            pointerOffsetLocal,
            initialAspectRatio: baseWidth / baseHeight,
        };

        originalSymbolStatesForResize.current.clear();
        originalLineStatesForResize.current.clear();
        endpointConnectionGroupsRef.current.clear();
        endpointToGroupRef.current.clear();

        editorStore.selections.forEach(sel => {
            if (isSymbolType(sel)) {
                const sym = floorplannerStore.symbolsMap.get(sel.id);
                if (sym) {
                    const keys = getSizeKeys(sym);
                    originalSymbolStatesForResize.current.set(sel.id, {
                        position: sym.position.clone(),
                        width: keys.wKey ? Number((sym as any)[keys.wKey]) : undefined,
                        height: keys.hKey ? Number((sym as any)[keys.hKey]) : undefined,
                    });
                }
            } else if (isWallType(sel) || isSingleLineType(sel) || isRulerLineType(sel)) {
                // Capture original endpoints, length, and symbol attachments for walls/lines/rulers
                const obj = floorplannerStore.findObjectId(sel.id);
                if (obj) {
                    const wall = isWallType(sel) ? floorplannerStore.wallsMap.get(sel.id) : null;
                    originalLineStatesForResize.current.set(sel.id, {
                        start: obj.start.clone(),
                        end: obj.end.clone(),
                        length: wall ? floorplannerStore.wallLength(wall) : obj.start.distanceTo(obj.end),
                        // Store original symbol attachments for walls so we can scale them proportionally
                        symbolAttachments: wall?.symbolAttachments?.map(att => ({
                            symbolId: att.symbolId,
                            positionFromStart: att.positionFromStart,
                        })),
                    });
                }
            }
            if (isWallType(sel)) {
                floorplannerStore.beginWallDrag(sel.id);
                // Optimize dragging by enabling progressive mode and visual updates
                wallUpdateManager.enableProgressiveModeForWall(sel.id, true);
                renderStore.allowVisualUpdatesForWall(sel.id);
                resizeDragWallIds.current.push(sel.id);
            }
        });

        // Build endpoint connection groups at drag start using ORIGINAL positions
        // This is done once, not on every move, for performance and consistency
        buildEndpointConnectionGroups();

        isResizing.current = true;

        /**
         * Builds connection groups from original endpoint positions using tight coordinate proximity.
         * Uses union-find to group endpoints that share a corner (within 0.001m tolerance).
         * Groups are stored in endpointConnectionGroupsRef and endpointToGroupRef.
         */
        function buildEndpointConnectionGroups() {
            const endpointGroups = new Map<string, string>(); // endpoint key -> group representative key

            // Helper to get endpoint key: wallId:start or wallId:end
            const getEndpointKey = (wallId: string, endpoint: "start" | "end") => `${wallId}:${endpoint}`;

            // Helper to find the group representative for an endpoint (with path compression)
            const findGroup = (key: string): string => {
                let current = key;
                const path: string[] = [];
                while (endpointGroups.has(current) && endpointGroups.get(current) !== current) {
                    path.push(current);
                    current = endpointGroups.get(current)!;
                }
                // Path compression: point all traversed nodes directly to the root
                path.forEach(node => endpointGroups.set(node, current));
                return current;
            };

            // Helper to union two endpoint groups
            const unionGroups = (key1: string, key2: string) => {
                const group1 = findGroup(key1);
                const group2 = findGroup(key2);
                if (group1 !== group2) {
                    endpointGroups.set(group2, group1);
                }
            };

            // Initialize each endpoint as its own group
            resizeDragWallIds.current.forEach(wallId => {
                const startKey = getEndpointKey(wallId, "start");
                const endKey = getEndpointKey(wallId, "end");
                endpointGroups.set(startKey, startKey);
                endpointGroups.set(endKey, endKey);
            });

            // Collect all original endpoints with their positions
            const allEndpoints: Array<{ wallId: string; endpoint: "start" | "end"; position: THREE.Vector2 }> = [];
            resizeDragWallIds.current.forEach(wallId => {
                const state = originalLineStatesForResize.current.get(wallId);
                if (state) {
                    allEndpoints.push({ wallId, endpoint: "start", position: state.start });
                    allEndpoints.push({ wallId, endpoint: "end", position: state.end });
                }
            });

            // Group endpoints that share the same corner using proximity tolerance
            // In edge-based wall systems, connected endpoints can be offset by up to wallWidth * sqrt(2)
            // at corners (perpendicular walls), so we need a tolerance that accounts for this.
            // Get the max wall width from the selected walls
            let maxWallWidth = 0.2; // Default wall width
            resizeDragWallIds.current.forEach(wallId => {
                const wall = floorplannerStore.wallsMap.get(wallId);
                if (wall?.wallWidth) {
                    maxWallWidth = Math.max(maxWallWidth, wall.wallWidth);
                }
            });
            // Tolerance: wallWidth * 1.5 to account for perpendicular corners plus some margin
            const PROXIMITY_TOLERANCE = maxWallWidth * 1.5;

            for (let i = 0; i < allEndpoints.length; i++) {
                for (let j = i + 1; j < allEndpoints.length; j++) {
                    const ep1 = allEndpoints[i];
                    const ep2 = allEndpoints[j];

                    const distance = ep1.position.distanceTo(ep2.position);
                    if (distance < PROXIMITY_TOLERANCE) {
                        const key1 = getEndpointKey(ep1.wallId, ep1.endpoint);
                        const key2 = getEndpointKey(ep2.wallId, ep2.endpoint);
                        unionGroups(key1, key2);
                    }
                }
            }

            // Build the final group structures
            // endpointConnectionGroupsRef: groupRepresentative -> Set of all endpoints in that group
            // endpointToGroupRef: endpoint -> its group representative
            const groupsMap = new Map<string, Set<string>>();

            allEndpoints.forEach(ep => {
                const key = getEndpointKey(ep.wallId, ep.endpoint);
                const representative = findGroup(key);

                endpointToGroupRef.current.set(key, representative);

                if (!groupsMap.has(representative)) {
                    groupsMap.set(representative, new Set());
                }
                groupsMap.get(representative)!.add(key);
            });

            endpointConnectionGroupsRef.current = groupsMap;
        }

        const onMove = (me: PointerEvent) => {
            if (!isResizing.current || !resizeStartRef.current) return;
            const { anchorLocal, center, angle, startBox, pointerOffsetLocal, initialAspectRatio } = resizeStartRef.current;
            const [wx, wy] = projectToWorld(me.clientX, me.clientY, gl, camera);

            // Convert pointer to local, apply offset, then measure new box in local space
            const cosA = Math.cos(-angle);
            const sinA = Math.sin(-angle);
            const dx = wx - center.x;
            const dy = wy - center.y;
            const pointerLocal = new THREE.Vector2(dx * cosA - dy * sinA, dx * sinA + dy * cosA);
            const draggedLocal = pointerLocal.clone().add(pointerOffsetLocal);

            // Lock aspect ratio by default; hold Shift or Alt for freehand
            const freehand = me.shiftKey || me.altKey;
            let newWidth = Math.max(Math.abs(draggedLocal.x - anchorLocal.x), 0.001);
            let newHeight = Math.max(Math.abs(draggedLocal.y - anchorLocal.y), 0.001);
            if (!freehand) {
                // Choose dominant axis scaling and derive the other using the initial aspect ratio
                const tentativeSx = newWidth / startBox.width;
                const tentativeSy = newHeight / startBox.height;
                if (tentativeSx > tentativeSy) {
                    newHeight = newWidth / initialAspectRatio;
                } else {
                    newWidth = newHeight * initialAspectRatio;
                }
            }
            const sxFactor = newWidth / startBox.width;
            const syFactor = newHeight / startBox.height;

            // Helper to convert world->local for current angle and center
            const worldToLocal = (wx: number, wy: number) => {
                const dx = wx - center.x;
                const dy = wy - center.y;
                const cos = Math.cos(-angle);
                const sin = Math.sin(-angle);
                return new THREE.Vector2(dx * cos - dy * sin, dx * sin + dy * cos);
            };

            // Helper to convert local->world
            const localToWorld = (lx: number, ly: number): THREE.Vector2 => {
                const cos = Math.cos(angle);
                const sin = Math.sin(angle);
                return new THREE.Vector2(
                    center.x + lx * cos - ly * sin,
                    center.y + lx * sin + ly * cos,
                );
            };

            // Helper to scale a point from anchor in local space
            const scalePoint = (point: THREE.Vector2): THREE.Vector2 => {
                const pLocal = worldToLocal(point.x, point.y);
                const pOff = new THREE.Vector2(pLocal.x - anchorLocal.x, pLocal.y - anchorLocal.y);
                const pScaled = new THREE.Vector2(pOff.x * sxFactor, pOff.y * syFactor);
                const pNewLocal = new THREE.Vector2(anchorLocal.x + pScaled.x, anchorLocal.y + pScaled.y);
                return localToWorld(pNewLocal.x, pNewLocal.y);
            };

            // Helper to get endpoint key
            const getEndpointKey = (wallId: string, endpoint: "start" | "end") => `${wallId}:${endpoint}`;

            const selectedWallIds = new Set(resizeDragWallIds.current);

            // ---- Scale connected endpoints together to maintain wall connections ----
            // In edge-based wall systems, connected endpoints are at DIFFERENT positions
            // (their edges meet, not their centerlines). We need to:
            // 1. Scale the "corner point" (centroid of the group)
            // 2. Maintain each endpoint's ORIGINAL OFFSET from that corner (don't scale the offset)
            // This preserves the edge-based connection geometry while scaling the room.

            // First, calculate the original centroid and scaled centroid for each group
            // Also store each endpoint's offset from its group's centroid
            const groupData = new Map<string, {
                originalCentroid: THREE.Vector2;
                scaledCentroid: THREE.Vector2;
                endpointOffsets: Map<string, THREE.Vector2>; // endpoint key -> offset from centroid
            }>();

            endpointConnectionGroupsRef.current.forEach((endpointKeys, groupRep) => {
                // Only process groups with 2+ endpoints (connected walls)
                if (endpointKeys.size < 2) return;

                // Calculate the centroid of original positions for this group
                let sumX = 0, sumY = 0;
                let count = 0;

                endpointKeys.forEach(key => {
                    const [wallId, endpoint] = key.split(':') as [string, "start" | "end"];
                    const state = originalLineStatesForResize.current.get(wallId);
                    if (state) {
                        const pos = endpoint === "start" ? state.start : state.end;
                        sumX += pos.x;
                        sumY += pos.y;
                        count++;
                    }
                });

                if (count > 0) {
                    const originalCentroid = new THREE.Vector2(sumX / count, sumY / count);
                    const scaledCentroid = scalePoint(originalCentroid);

                    // Calculate each endpoint's offset from the original centroid
                    const endpointOffsets = new Map<string, THREE.Vector2>();
                    endpointKeys.forEach(key => {
                        const [wallId, endpoint] = key.split(':') as [string, "start" | "end"];
                        const state = originalLineStatesForResize.current.get(wallId);
                        if (state) {
                            const pos = endpoint === "start" ? state.start : state.end;
                            // Store the offset (NOT scaled) - this preserves edge-based connection geometry
                            const offset = new THREE.Vector2(
                                pos.x - originalCentroid.x,
                                pos.y - originalCentroid.y
                            );
                            endpointOffsets.set(key, offset);
                        }
                    });

                    groupData.set(groupRep, {
                        originalCentroid,
                        scaledCentroid,
                        endpointOffsets,
                    });
                }
            });

            // Now update all walls using the group data
            // For endpoints in a group of 2+, apply: scaledCentroid + originalOffset
            // For isolated endpoints, scale them individually
            resizeDragWallIds.current.forEach(wallId => {
                const state = originalLineStatesForResize.current.get(wallId);
                if (!state) return;

                const startKey = getEndpointKey(wallId, "start");
                const endKey = getEndpointKey(wallId, "end");

                const startGroupRep = endpointToGroupRef.current.get(startKey);
                const endGroupRep = endpointToGroupRef.current.get(endKey);

                // Get the group data for each endpoint
                const startGroupData = startGroupRep ? groupData.get(startGroupRep) : null;
                const endGroupData = endGroupRep ? groupData.get(endGroupRep) : null;

                let newStart: THREE.Vector2;
                let newEnd: THREE.Vector2;

                // For endpoints in a connected group, use scaledCentroid + originalOffset
                // This maintains the edge-based connection geometry
                if (startGroupData && startGroupData.endpointOffsets.has(startKey)) {
                    const offset = startGroupData.endpointOffsets.get(startKey)!;
                    newStart = new THREE.Vector2(
                        startGroupData.scaledCentroid.x + offset.x,
                        startGroupData.scaledCentroid.y + offset.y
                    );
                } else {
                    newStart = scalePoint(state.start);
                }

                if (endGroupData && endGroupData.endpointOffsets.has(endKey)) {
                    const offset = endGroupData.endpointOffsets.get(endKey)!;
                    newEnd = new THREE.Vector2(
                        endGroupData.scaledCentroid.x + offset.x,
                        endGroupData.scaledCentroid.y + offset.y
                    );
                } else {
                    newEnd = scalePoint(state.end);
                }

                floorplannerStore.updateObjectPosition(
                    wallId,
                    newStart,
                    newEnd,
                    { bypassEdgeConstraints: true, skipInvalidation: true }
                );

                // Scale symbol attachments proportionally based on the wall's new length
                const wall = floorplannerStore.wallsMap.get(wallId);
                if (wall && state.symbolAttachments && wall.symbolAttachments) {
                    const oldLength = state.length;
                    const newLength = floorplannerStore.wallLength(wall);
                    const lengthScale = oldLength > 0.001 ? newLength / oldLength : 1;
                    const originalPositionBySymbolId = new Map(
                        state.symbolAttachments.map((attachment) => [
                            attachment.symbolId,
                            attachment.positionFromStart,
                        ])
                    );

                    // Update each attachment's positionFromStart proportionally, matched by symbol ID.
                    wall.symbolAttachments.forEach((attachment) => {
                        const originalPosition = originalPositionBySymbolId.get(attachment.symbolId);
                        if (originalPosition !== undefined) {
                            attachment.positionFromStart = originalPosition * lengthScale;
                        }
                    });
                }

                // Update symbol positions based on new wall geometry
                updateSymbolAttachmentsDuringDrag(wallId, floorplannerStore);
            });

            // Also update single lines and ruler lines (which don't have edge connections)
            originalLineStatesForResize.current.forEach((state, id) => {
                // Skip walls - they were handled above
                if (selectedWallIds.has(id)) return;

                // For non-walls, use simple coordinate-based scaling
                floorplannerStore.updateObjectPosition(
                    id,
                    scalePoint(state.start),
                    scalePoint(state.end),
                    { bypassEdgeConstraints: true, skipInvalidation: true }
                );
            });

            // Trigger wall geometry update and visual refresh for selected walls
            resizeDragWallIds.current.forEach(wallId => {
                floorplannerStore.incrementWallGeometryVersion(wallId);
                wallUpdateManager.updateWall(wallId);
            });

            // Update edge connections incrementally during resize for better visual feedback
            // This ensures the joint rendering stays correct while dragging
            resizeDragWallIds.current.forEach(wallId => {
                const wall = floorplannerStore.wallsMap.get(wallId) as WallType | undefined;
                if (!wall?.edgeConnections) return;

                const updatedEdgeConnections = wall.edgeConnections.map(edgeConn => {
                    // Only update if target wall is also being resized
                    if (!selectedWallIds.has(edgeConn.targetWallId)) {
                        return edgeConn;
                    }

                    const targetWall = floorplannerStore.wallsMap.get(edgeConn.targetWallId) as WallType | undefined;
                    if (!targetWall) return edgeConn;

                    // Skip middle-point connections (T-junctions)
                    if (edgeConn.targetEndpoint === "middle" || typeof edgeConn.targetEndpoint === "number") {
                        return edgeConn;
                    }

                    try {
                        const updatedConnection = createEdgeConnection(
                            wall,
                            edgeConn.sourceEndpoint as "start" | "end",
                            targetWall,
                            edgeConn.targetEndpoint,
                            undefined,
                            edgeConn.sourceSide || edgeConn.targetSide
                                ? {
                                    sourceSide: edgeConn.sourceSide,
                                    targetSide: edgeConn.targetSide,
                                }
                                : undefined
                        );
                        return {
                            ...updatedConnection,
                            id: edgeConn.id,
                        };
                    } catch {
                        return edgeConn;
                    }
                });

                wall.edgeConnections = updatedEdgeConnections;
            });

            // Update symbols
            originalSymbolStatesForResize.current.forEach((state, id) => {
                // Scale position from anchor in local space
                const posLocal = worldToLocal(state.position.x, state.position.y);
                const offLocal = new THREE.Vector2(posLocal.x - anchorLocal.x, posLocal.y - anchorLocal.y);
                const scaledLocal = new THREE.Vector2(offLocal.x * sxFactor, offLocal.y * syFactor);
                const newLocal = new THREE.Vector2(anchorLocal.x + scaledLocal.x, anchorLocal.y + scaledLocal.y);
                const newWorld = localToWorld(newLocal.x, newLocal.y);
                floorplannerStore.updateSymbolPositionRespectingAttachment(id, [newWorld.x, newWorld.y]);

                // Scale size if available
                const sym = floorplannerStore.symbolsMap.get(id);
                if (sym) {
                    const keys = getSizeKeys(sym);
                    if (keys.wKey && state.width !== undefined) {
                        floorplannerStore.updateSymbolProperty(id, keys.wKey as any, Math.max(state.width * sxFactor, 0.06));
                    }
                    if (keys.hKey && state.height !== undefined) {
                        floorplannerStore.updateSymbolProperty(id, keys.hKey as any, Math.max(state.height * syFactor, 0.06));
                    }
                }
            });

            // Update session box dimensions AND center to keep overlay/handles aligned during resize
            if (rotationSessionBoxRef.current) {
                rotationSessionBoxRef.current.width = startBox.width * sxFactor;
                rotationSessionBoxRef.current.height = startBox.height * syFactor;
                // New center (local) after scaling from anchor: c' = anchor + (c - anchor) * scale, with c=(0,0)
                const newCenterLocal = new THREE.Vector2(
                    anchorLocal.x * (1 - sxFactor),
                    anchorLocal.y * (1 - syFactor)
                );
                const newCenterWorld = localToWorld(newCenterLocal.x, newCenterLocal.y);
                rotationSessionBoxRef.current.center = new THREE.Vector3(newCenterWorld.x, newCenterWorld.y, 0);
            }
            if (rotateStartBoxRef.current) {
                rotateStartBoxRef.current.width = startBox.width * sxFactor;
                rotateStartBoxRef.current.height = startBox.height * syFactor;
                const newCenterLocal = new THREE.Vector2(
                    anchorLocal.x * (1 - sxFactor),
                    anchorLocal.y * (1 - syFactor)
                );
                const newCenterWorld = localToWorld(newCenterLocal.x, newCenterLocal.y);
                rotateStartBoxRef.current.center.set(newCenterWorld.x, newCenterWorld.y, 0);
            }
        };

        const onUp = () => {
            isResizing.current = false;
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);

            // Record final state of all resized objects for undo
            const changedAttachmentWallIds = new Set<string>();
            editorStore.selections.forEach(sel => {
                if (isSymbolType(sel) || isWallType(sel) || isSingleLineType(sel) || isRulerLineType(sel)) {
                    if (isSymbolType(sel)) {
                        const attachedWall = floorplannerStore.symbolIsAttached(sel.id);
                        if (attachedWall && !selectedWallIdsAtResizeStart.has(attachedWall)) {
                            changedAttachmentWallIds.add(attachedWall);
                        }
                    }
                    floorplannerStore.recordObjectChange(sel.id, 'modify');
                }
            });
            changedAttachmentWallIds.forEach((wallId) => {
                floorplannerStore.recordWallChange(wallId, 'modify');
            });
            floorplannerStore.pushToUndoStack();

            floorplannerStore.setDirty();
            renderStore.setIsDragging(false);

            if (resizeDragWallIds.current.length > 0) {
                const affected = new Set<string>();
                const selectedWallIds = new Set(resizeDragWallIds.current);

                resizeDragWallIds.current.forEach(id => {
                    // Disable progressive mode and finalize updates
                    wallUpdateManager.enableProgressiveModeForWall(id, false);

                    const impacted = floorplannerStore.endWallDrag(id);
                    impacted.forEach(w => affected.add(w));

                    // Regenerate outline segments for each resized wall
                    // This ensures proper visual rendering after the resize
                    renderStore.generateOutlineSegments(id);
                });

                // Recalculate edge connections after resize completes
                // This is done once at the end rather than on every mouse move
                resizeDragWallIds.current.forEach(wallId => {
                    const wall = floorplannerStore.wallsMap.get(wallId) as WallType | undefined;
                    if (!wall || !wall.edgeConnections) return;

                    const updatedEdgeConnections = wall.edgeConnections.map(edgeConn => {
                        // Only update if target wall is also being resized
                        if (!selectedWallIds.has(edgeConn.targetWallId)) {
                            return edgeConn;
                        }

                        const targetWall = floorplannerStore.wallsMap.get(edgeConn.targetWallId) as WallType | undefined;
                        if (!targetWall) return edgeConn;

                        try {
                            const updatedConnection = createEdgeConnection(
                                wall,
                                edgeConn.sourceEndpoint as "start" | "end",
                                targetWall,
                                edgeConn.targetEndpoint,
                                undefined,
                                edgeConn.sourceSide || edgeConn.targetSide
                                    ? {
                                        sourceSide: edgeConn.sourceSide,
                                        targetSide: edgeConn.targetSide,
                                    }
                                    : undefined
                            );
                            return {
                                ...updatedConnection,
                                id: edgeConn.id,
                            };
                        } catch (error) {
                            return edgeConn;
                        }
                    });

                    wall.edgeConnections = updatedEdgeConnections;
                    floorplannerStore.incrementWallGeometryVersion(wallId);
                });

                // Only update walls that were directly resized, not all affected walls
                // This prevents the cascade performance issue
                resizeDragWallIds.current.forEach(wallId => {
                    floorplannerStore.objectNeedsUpdate(wallId);
                });

                resizeDragWallIds.current = [];
            }
        };

        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
    }, [selectionIndependent, groupBox, gl, camera]);

    // Reset rotation session when selection changes or is cleared
    const prevSelectionKeyRef = useRef<string>("");
    useEffect(() => {
        const key = editorStore.selections.map(s => s.id).sort().join('|');
        if (editorStore.selections.length === 0 || (prevSelectionKeyRef.current && prevSelectionKeyRef.current !== key)) {
            rotationSessionAngleRef.current = 0;
            rotationSessionBoxRef.current = null;
            setRotationPreview(0);
        }
        prevSelectionKeyRef.current = key;
    }, [editorStore.selections]);

    useEffect(() => {
        const isTranslating = renderStore.isGroupTranslating;
        const wasTranslating = wasGroupTranslatingRef.current;

        if (isTranslating && !wasTranslating) {
            const bounds = getSelectionBounds(editorStore.selections.filter(isSelectionBoundsItem));
            translateStartCenterRef.current = bounds ? bounds.center.clone() : null;
        }

        if (!isTranslating && wasTranslating) {
            if (
                translateStartCenterRef.current &&
                rotationSessionBoxRef.current &&
                isRotationSessionActive(rotationSessionAngleRef.current)
            ) {
                const bounds = getSelectionBounds(editorStore.selections.filter(isSelectionBoundsItem));
                if (bounds) {
                    const deltaX = bounds.center.x - translateStartCenterRef.current.x;
                    const deltaY = bounds.center.y - translateStartCenterRef.current.y;
                    if (Math.abs(deltaX) > ROTATION_SESSION_EPSILON || Math.abs(deltaY) > ROTATION_SESSION_EPSILON) {
                        rotationSessionBoxRef.current.center.add(new THREE.Vector3(deltaX, deltaY, 0));
                    }
                }
            }
            translateStartCenterRef.current = null;
        }

        wasGroupTranslatingRef.current = isTranslating;
    }, [renderStore.isGroupTranslating, editorStore.selections]);

    const rotationSessionAngle = rotationSessionAngleRef.current || 0;
    const hasRotationSession = isRotationSessionActive(rotationSessionAngle);
    const selectionHandlesVisible = editorStore.shouldShowSelectionHandles();

    // Validate plane size to prevent invalid geometry
    const planeSize = MAX_INTERACTION_PLANE_SIZE * 1.5;
    const isValidSize = Number.isFinite(planeSize) && planeSize > 0 && planeSize <= 10000;
    
    if (!isValidSize && process.env.NODE_ENV === 'development') {
        console.warn(`[SelectionPlane] Invalid plane size: ${planeSize}, using default 450`);
    }

    return (
        <>
            <mesh
                position={[0, 0, -10]} // Keep canvas selection behind backdrop images and drawing elements.
                onPointerDown={(e) => {
                    if (getBackdropHitTarget(e.clientX, e.clientY, gl, camera)) {
                        return;
                    }
                    handlePointerDown(e.nativeEvent);
                }}
                onDoubleClick={(e) => {
                    if (getBackdropHitTarget(e.clientX, e.clientY, gl, camera)) {
                        return;
                    }
                    handleDoubleClick(e);
                }}
                userData={{ id: 'selection-plane' }}
            >
                <planeGeometry args={[isValidSize ? planeSize : 450, isValidSize ? planeSize : 450]} />
                <meshBasicMaterial transparent opacity={0} />
            </mesh>

            {/* Render transparent blue selection box while drawing */}
            {isDrawing && selectionRectanglePoints.length > 0 && (() => {
                const width = Math.abs(selectionRectanglePoints[2].x - selectionRectanglePoints[0].x);
                const height = Math.abs(selectionRectanglePoints[2].y - selectionRectanglePoints[0].y);
                const centerX = (selectionRectanglePoints[0].x + selectionRectanglePoints[2].x) / 2;
                const centerY = (selectionRectanglePoints[0].y + selectionRectanglePoints[2].y) / 2;

                if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
                    return null;
                }

                return (
                    <mesh
                        position={[centerX, centerY, 0.3]}
                        geometry={new THREE.PlaneGeometry(width, height)}
                    >
                        <meshBasicMaterial color={CANVAS_ACCENT_COLOR} transparent opacity={0.2} />
                    </mesh>
                );
            })()}

            {/* Render the selection rectangle outline while drawing */}
            {isDrawing && selectionRectanglePoints.length > 0 && (
                <Line
                    position={[0, 0, 0.3]} // Above everything
                    points={selectionRectanglePoints}
                    color="darkblue"
                    lineWidth={1}
                    raycast={noopRaycast}
                />
            )}

            {/* Render the final bounding box around selected items */}
            {boundingBoxPoints.length > 0 && (
                <group position={[0, 0, 0.02]}>
                    {(rotationPreview === 0 && !hasRotationSession && !renderStore.isGroupTranslating) && (
                        <Line
                            points={boundingBoxPoints}
                            color="#0066FF"
                            lineWidth={1}
                            transparent={false}
                            depthTest={false}
                            renderOrder={9999}
                            raycast={noopRaycast}
                        />
                    )}
                    {groupBox && Number.isFinite(groupBox.width) && Number.isFinite(groupBox.height) && (
                        <>
                            {/* Calculate translate offset for group drag */}
                            {(() => {
                                const translateOffset = renderStore.isGroupTranslating
                                    ? renderStore.groupTranslateOffset
                                    : { x: 0, y: 0 };
                                const offsetX = translateOffset.x;
                                const offsetY = translateOffset.y;
                                const activeBox = rotateStartBoxRef.current
                                    ?? (hasRotationSession ? rotationSessionBoxRef.current : null)
                                    ?? groupBox;
                                const activeWidth = activeBox.width;
                                const activeHeight = activeBox.height;
                                const activeCenter = activeBox.center;
                                const activeRotation = (hasRotationSession ? rotationSessionAngle : 0) + rotationPreview;
                                return (
                                    <>
                                        {/* Transparent plane over the group box to allow moving the entire selection */}
                                        <mesh
                                            position={[groupBox.center.x + offsetX, groupBox.center.y + offsetY, 0.021]}
                                            onPointerDown={onGroupPlanePointerDown}
                                        >
                                            <planeGeometry args={[groupBox.width, groupBox.height]} />
                                            <meshBasicMaterial transparent opacity={0} />
                                        </mesh>

                                        {/* Rotating overlay: during rotation, preview the box rotated */}
                                        {/* Hide handles during group translate */}
                                        <group position={[activeCenter.x + offsetX, activeCenter.y + offsetY, 0.022]} rotation={[0, 0, activeRotation]}>
                                            {/* Rotated bounding box */}
                                            <Line
                                                points={[
                                                    new THREE.Vector3(-activeWidth / 2, -activeHeight / 2, 0),
                                                    new THREE.Vector3(activeWidth / 2, -activeHeight / 2, 0),
                                                    new THREE.Vector3(activeWidth / 2, activeHeight / 2, 0),
                                                    new THREE.Vector3(-activeWidth / 2, activeHeight / 2, 0),
                                                    new THREE.Vector3(-activeWidth / 2, -activeHeight / 2, 0),
                                                ]}
                                                color="#0066FF"
                                                lineWidth={1}
                                                transparent={false}
                                                depthTest={false}
                                                renderOrder={9999}
                                                raycast={noopRaycast}
                                            />

                                            {/* Group Rotate Handle (disabled if any wall is selected) - hidden during translate */}
                                            {selectionHandlesVisible && selectionIndependent && !renderStore.isGroupTranslating && (
                                                <group position={[0, (activeHeight / 2) + (0.25 / editorStore.zoomLevelDivisor()), SELECTED_HANDLE_INTERACTION_Z]}>
                                                    {/* Larger hit area, visual icon via Html */}
                                                    <mesh onPointerDown={beginGroupRotate} renderOrder={SELECTED_HANDLE_RENDER_ORDER}>
                                                        <circleGeometry args={[0.15 / editorStore.zoomLevelDivisor(), 24]} />
                                                        <meshBasicMaterial transparent opacity={0} depthTest={false} depthWrite={false} />
                                                    </mesh>
                                                    <Html
                                                        center
                                                        zIndexRange={CANVAS_HTML_HANDLE_Z_INDEX_RANGE}
                                                        style={{ pointerEvents: 'none' }}
                                                    >
                                                        <FaRotate size={18} color={'black'} />
                                                    </Html>
                                                </group>
                                            )}

                                            {/* Group Resize Handles on corners (disabled if any wall is selected) - hidden during translate */}
                                            {(rotationPreview === 0) && selectionHandlesVisible && selectionIndependent && !renderStore.isGroupTranslating && (
                                                <group>
                                                    <mesh position={[-(activeWidth / 2), -(activeHeight / 2), SELECTED_HANDLE_INTERACTION_Z]} onPointerDown={(e) => beginGroupResize('bottomLeft', e)} renderOrder={SELECTED_HANDLE_RENDER_ORDER}>
                                                        <circleGeometry args={[0.1 / editorStore.zoomLevelDivisor(), 20]} />
                                                        <meshBasicMaterial color={CANVAS_ACCENT_COLOR} depthTest={false} depthWrite={false} />
                                                    </mesh>
                                                    <mesh position={[activeWidth / 2, -(activeHeight / 2), SELECTED_HANDLE_INTERACTION_Z]} onPointerDown={(e) => beginGroupResize('bottomRight', e)} renderOrder={SELECTED_HANDLE_RENDER_ORDER}>
                                                        <circleGeometry args={[0.1 / editorStore.zoomLevelDivisor(), 20]} />
                                                        <meshBasicMaterial color={CANVAS_ACCENT_COLOR} depthTest={false} depthWrite={false} />
                                                    </mesh>
                                                    <mesh position={[-(activeWidth / 2), activeHeight / 2, SELECTED_HANDLE_INTERACTION_Z]} onPointerDown={(e) => beginGroupResize('topLeft', e)} renderOrder={SELECTED_HANDLE_RENDER_ORDER}>
                                                        <circleGeometry args={[0.1 / editorStore.zoomLevelDivisor(), 20]} />
                                                        <meshBasicMaterial color={CANVAS_ACCENT_COLOR} depthTest={false} depthWrite={false} />
                                                    </mesh>
                                                    <mesh position={[activeWidth / 2, activeHeight / 2, SELECTED_HANDLE_INTERACTION_Z]} onPointerDown={(e) => beginGroupResize('topRight', e)} renderOrder={SELECTED_HANDLE_RENDER_ORDER}>
                                                        <circleGeometry args={[0.1 / editorStore.zoomLevelDivisor(), 20]} />
                                                        <meshBasicMaterial color={CANVAS_ACCENT_COLOR} depthTest={false} depthWrite={false} />
                                                    </mesh>
                                                </group>
                                            )}
                                        </group>
                                    </>
                                );
                            })()}
                        </>
                    )}
                </group>
            )}
        </>
    );
});

export default SelectionPlane;
